import { MapStringTo } from "../base-types";
import { IExtendedContentHubClient } from "../clients/extended-client";
import { Entity, IEntity } from "../contracts/base/entity";
import { EntityConstructionArgs } from "../contracts/base/entity-construction-args";
import { MinimalSchema } from "../contracts/querying/minimal-schema";
import Guard from "../guard";
import { EntityResource } from "../models/entity-resource";
import { RelationResource } from "../models/relation-resource";
import { PropertyMapper } from "./property-mapper";
import { RelatedPathMapper } from "./related-path-mapper";
import { RelationMapper } from "./relation-mapper";
import { RenditionMapper } from "./rendition-mapper";

export class EntityMapper {
    private readonly _client: IExtendedContentHubClient;
    private readonly _propertyMapper: PropertyMapper;
    private readonly _relationMapper: RelationMapper;
    private readonly _renditionMapper: RenditionMapper;
    private readonly _relatedPathMapper: RelatedPathMapper;

    constructor(client: IExtendedContentHubClient) {
        Guard.notNullOrUndefined(client);

        this._client = client;
        this._propertyMapper = new PropertyMapper(client);
        this._relationMapper = new RelationMapper(client);
        this._renditionMapper = new RenditionMapper(client);
        this._relatedPathMapper = new RelatedPathMapper();
    }

    async mapEntityAsync(resource: EntityResource, schema?: MinimalSchema): Promise<IEntity> {
        Guard.notNullOrUndefined(resource);

        // The minimal schema is optional, but we should at least have a definition name.
        const definitionName =
            schema?.definitionName ??
            (await this._client.linkHelper.nameFromDefinitionAsync(resource.entityDefinition));
        Guard.notNullOrUndefined(definitionName);
        Guard.stringNotNullOrEmpty(definitionName);

        const cultures = new Set(resource.cultures || []); //? Revisit usage of set
        const properties = await this._propertyMapper.mapPropertiesAsync(resource, [...cultures], schema);
        const relations = await this._relationMapper.mapRelationsAsync(resource, schema);
        const renditions = this._renditionMapper.mapRenditions(resource.id, resource); //TODO: Extract extension data?
        const relatedPaths = this._relatedPathMapper.mapRelatedPaths(resource);

        const args = new EntityConstructionArgs(definitionName!, {
            id: resource.id || 0,
            identifier: resource.identifier!,
            cultures: [...cultures],
            properties: properties,
            relations: relations,
            renditions: renditions,
            relatedPaths: relatedPaths,
        });

        const entity = new Entity(this._client, args);
        await this.mapBasePropertiesToSdkEntityAsync(resource, entity);
        entity.startTracking();

        //TODO: Extension data

        return entity;
    }

    async mapEntitiesAsync(
        collection: Array<EntityResource>,
        schemas: MapStringTo<MinimalSchema>
    ): Promise<Array<IEntity>> {
        Guard.notNullOrUndefined(collection, "collection");
        Guard.notNullOrUndefined(schemas, "schemas");

        const list: Array<IEntity> = [];
        for (const resource of collection) {
            if (resource == null) continue;

            const definitionName = (await this._client.linkHelper.nameFromDefinitionAsync(resource.entityDefinition))!;

            if (!schemas[definitionName]) {
                throw Error(`Cannot map entity, missing minimal schema for definition '${definitionName}'.`);
            }

            const schema = schemas[definitionName];
            const entity = await this.mapEntityAsync(resource, schema);

            list.push(entity);
        }

        return list;
    }

    async toResourceAsync(entity: IEntity): Promise<EntityResource> {
        Guard.notNullOrUndefined(entity);

        const properties = PropertyMapper.mapToPropertyResources(entity);
        const relations = await this._relationMapper.mapToRelationResourcesAsync(entity);
        const renditions = this._renditionMapper.mapToResource(entity.renditions);

        const resource = await this.createEntityResourceAsync(entity, properties, relations, renditions);

        return resource;
    }

    async mapDirtiesToResourceAsync(entity: IEntity): Promise<EntityResource> {
        Guard.notNullOrUndefined(entity);

        const properties = PropertyMapper.mapDirtyProperties(entity);
        const relations = await this._relationMapper.mapDirtyRelationsAsync(entity);
        const renditions = this._renditionMapper.mapToResource(entity.renditions);

        const resource = await this.createEntityResourceAsync(entity, properties, relations, renditions);

        return resource;
    }

    private async createEntityResourceAsync(
        entity: IEntity,
        properties: MapStringTo<unknown>,
        relations: MapStringTo<RelationResource>,
        renditions: MapStringTo<unknown>
    ): Promise<EntityResource> {
        const definitionLink = await this._client.linkHelper.definitionToLinkAsync(entity.definitionName);
        const entityLink = entity.id ? await this._client.linkHelper.entityToLinkAsync(entity.id) : undefined;
        const createdBy = entity.createdBy
            ? await this._client.linkHelper.entityToLinkAsync(entity.createdBy)
            : undefined;
        const lockedBy = entity.lockedBy ? await this._client.linkHelper.entityToLinkAsync(entity.lockedBy) : undefined;
        const modifiedBy = entity.modifiedBy
            ? await this._client.linkHelper.entityToLinkAsync(entity.modifiedBy)
            : undefined;

        const relatedPaths = this._relatedPathMapper.mapToResource(entity);

        const resource = new EntityResource({
            entityDefinition: definitionLink,
            id: entity.id || 0,
            identifier: entity.identifier,
            cultures: entity.cultures.map(x => x),
            isRootTaxonomyItem: entity.isRootTaxonomyItem,
            isPathRoot: entity.isPathRoot,
            inheritsSecurity: entity.inheritsSecurity,
            isSystemOwned: entity.isSystemOwned,
            properties: properties,
            relations: relations,
            renditions: renditions,
            isCurrentUserDefault: entity.isCurrentUserDefault,
            languageSupportedOotb: entity.languageSupportedOotb,
            hasPublicLink: entity.hasPublicLink,
            annotationCount: entity.annotationCount,
            masterFileModifiedOn: entity.masterFileModifiedOn,
            savedSelectionPageName: entity.savedSelectionPageName,
            gatewayLinks: entity.gatewayLinks,
            publicLink: entity.publicLink,
            publicCollectionLink: entity.publicCollectionLink,
            isEnabled: entity.isEnabled,
            modules: entity.modules,
            roles: entity.roles,
            permissions: entity.permissions,
            path: entity.path,
            relatedPaths: relatedPaths,
            lockedOn: entity.lockedOn,
            lockedBy: lockedBy,
            createdOn: entity.createdOn,
            createdBy: createdBy,
            modifiedOn: entity.modifiedOn,
            modifiedBy: modifiedBy,
            version: entity.version,
            self: entityLink,
            combinedPublishStatus: entity.combinedPublishStatus,
            combinedPublishStatusDetails: entity.combinedPublishStatusDetails,
            externalDataIdentifier: entity.externalDataIdentifier,
        });

        return resource;
    }

    private async mapBasePropertiesToSdkEntityAsync(resource: EntityResource, entity: IEntity): Promise<void> {
        const createdBy = resource.createdBy && (await this._client.linkHelper.idFromEntityAsync(resource.createdBy));
        const lockedBy = resource.lockedBy ? await this._client.linkHelper.idFromEntityAsync(resource.lockedBy) : null;
        const modifiedBy =
            resource.modifiedBy && (await this._client.linkHelper.idFromEntityAsync(resource.modifiedBy));

        //? Create deep copies
        entity.inheritsSecurity = resource.inheritsSecurity;
        entity.isSystemOwned = resource.isSystemOwned;
        entity.lockedOn = resource.lockedOn || null;
        entity.lockedBy = lockedBy;
        entity.createdOn = resource.createdOn;
        entity.createdBy = createdBy || undefined;
        entity.modifiedOn = resource.modifiedOn;
        entity.modifiedBy = modifiedBy || undefined;
        entity.version = resource.version;
        entity.isPathRoot = resource.isPathRoot;
        entity.isRootTaxonomyItem = resource.isRootTaxonomyItem;
        entity.isCurrentUserDefault = resource.isCurrentUserDefault;
        entity.languageSupportedOotb = resource.languageSupportedOotb;
        entity.hasPublicLink = resource.hasPublicLink;
        entity.annotationCount = resource.annotationCount;
        entity.masterFileModifiedOn = resource.masterFileModifiedOn;
        entity.savedSelectionPageName = resource.savedSelectionPageName;
        entity.gatewayLinks = resource.gatewayLinks;
        entity.publicLink = resource.publicLink;
        entity.publicCollectionLink = resource.publicCollectionLink;
        entity.isEnabled = resource.isEnabled;
        entity.modules = resource.modules;
        entity.roles = resource.roles;
        entity.permissions = resource.permissions;
        entity.path = resource.path;
        entity.combinedPublishStatus = resource.combinedPublishStatus;
        entity.combinedPublishStatusDetails = resource.combinedPublishStatusDetails;
        entity.externalDataIdentifier = resource.externalDataIdentifier;
    }
}
